import { SchoolTestDomain } from '@mathflat/domain/@entities/(Content)/SchoolTest/domain'
import type {
  GradeHigh,
  NumberGradeMiddle,
  Semester,
} from '@mathflat/domain/@entities/SchoolSystem/schema'
import { SchoolSystem } from '@mathflat/domain/@entities/SchoolSystem/SchoolSystem'
import { action, computed, makeObservable, observable } from 'mobx'

import { DTO, SchoolGrade } from '@/dto'
import { studentUtils } from '@/utils/studentUtils'

export const formatSubjectToGradeSemester = (
  subject: GradeHigh,
): {
  grade: string
  semester: string
} => {
  switch (subject) {
    case '고등수학(상)':
    case '공통수학1':
      return { grade: '1', semester: '1' }
    case '고등수학(하)':
    case '공통수학2':
      return { grade: '1', semester: '2' }
    case '수학 I':
    case '수학 II':
    case '기하':
      return { grade: '2', semester: '1' }
    case '확률과 통계':
    case '미적분':
    case '대수':
    case '미적분1':
    case '미적분2':
      return { grade: '2', semester: '2' }

    default:
      return { grade: '', semester: '' }
  }
}

// 학교별 기출 시험지 목록(테마별, 기출업로드)
export class SchoolTest extends DTO {
  id: string
  schoolCode: res.SchoolTest['schoolCode']
  schoolName: res.SchoolTest['schoolName']
  revision: res.SchoolTest['revision']
  region: res.SchoolTest['region']
  schoolTestYear: res.SchoolTest['schoolTestYear']
  schoolTestType: res.SchoolTest['schoolTestType']
  schoolType: res.SchoolTest['schoolType']
  grade: res.SchoolTest['grade']
  semester: res.SchoolTest['semester']
  /**
   * 원본 기출 시험지
   */
  schoolTestWorkbook: SchoolTestDomain.SchoolTestOrigin | null = null

  // [학교명(축약)]+[학년-학기]+[과목(축약)]+[시험유형(+과목명)]+[연도]
  get title() {
    if (this.schoolType === 'HIGH') {
      const { enforcementGrade, enforcementSemester } = this.schoolTestWorkbook ?? {}
      const { grade, semester } =
        enforcementGrade != null && enforcementSemester != null
          ? { grade: String(enforcementGrade), semester: String(enforcementSemester) }
          : formatSubjectToGradeSemester(this.grade as GradeHigh)

      const subjectLabel =
        this.grade === '확률과 통계'
          ? this.grade
          : SchoolGrade.getKoreanHighSubjectShort(this.grade as GradeHigh)
      return [
        SchoolSystem.shortenSchoolName(this.schoolName),
        grade && semester ? `${grade}-${semester}` : undefined,
        subjectLabel,
        SchoolTestDomain.SCHOOL_TEST_TERM_KOR[this.schoolTestType],
        `${this.schoolTestYear}`,
      ]
        .filter(Boolean)
        .join(' ')
    }

    const enforcementGrade = this.schoolTestWorkbook?.enforcementGrade
    const enforcementSemester = this.schoolTestWorkbook?.enforcementSemester
    const gradeSemester =
      enforcementGrade != null && enforcementSemester != null
        ? `${enforcementGrade}-${enforcementSemester}`
        : `${this.grade}-${this.semester}`

    return [
      SchoolSystem.shortenSchoolName(this.schoolName),
      gradeSemester,
      SchoolTestDomain.SCHOOL_TEST_TERM_KOR[this.schoolTestType],
      `${this.schoolTestYear}`,
    ].join(' ')
  }

  get schoolTypeInShortKor() {
    return studentUtils.getStudentSchoolTypeInKor(this.schoolType)
  }

  get hasPdf() {
    return !!this.schoolTestWorkbook
  }

  // 기출 시험지의 시행 학년/학기.
  // 1) workbook의 enforcementGrade/Semester가 있으면 우선 사용
  // 2) MIDDLE: schoolTest.grade/semester를 숫자로 사용
  // 3) HIGH: grade가 과목명이므로 formatSubjectToGradeSemester로 매핑
  get enforcementGradeSemester(): {
    enforcementGrade: NumberGradeMiddle | null
    enforcementSemester: Semester | null
  } {
    const workbook = this.schoolTestWorkbook
    if (workbook?.enforcementGrade != null && workbook?.enforcementSemester != null) {
      return {
        enforcementGrade: workbook.enforcementGrade as NumberGradeMiddle,
        enforcementSemester: workbook.enforcementSemester,
      }
    }

    const { grade, semester } =
      this.schoolType === 'HIGH'
        ? formatSubjectToGradeSemester(this.grade as GradeHigh)
        : { grade: String(this.grade), semester: String(this.semester) }

    return {
      enforcementGrade: (Number(grade) || null) as NumberGradeMiddle | null,
      enforcementSemester: (Number(semester) || null) as Semester | null,
    }
  }

  /**
   * 기출 원본 PDF 업로드 가능 여부
   * - 수급한 기출 시험지가 없거나(null)
   * - 수급한 기출 시험지가 있으나 사용자에게 PDF를 공개하지 않는 상태(SIMILAR_ONLY, NONE)
   */
  get isUserUploadable() {
    const USER_PROVIDE_TYPES: SchoolTestDomain.ProvideType[] = ['ALL', 'PDF_ONLY']
    return (
      !this.schoolTestWorkbook || !USER_PROVIDE_TYPES.includes(this.schoolTestWorkbook.provideType)
    )
  }

  updatePdfAccessed() {
    const schoolTestWorkbook = this.schoolTestWorkbook
    if (!schoolTestWorkbook) return
    schoolTestWorkbook.pdfDownloaded = true
  }

  constructor(data: res.SchoolTest) {
    super()
    makeObservable(this, {
      schoolTestWorkbook: observable.deep,
      hasPdf: computed,
      isUserUploadable: computed,
      enforcementGradeSemester: computed,
      updatePdfAccessed: action.bound,
    })

    this.id = [
      data.schoolCode,
      data.schoolTestYear,
      data.schoolType,
      data.grade,
      data.semester,
      data.revision,
      data.schoolTestType,
      data.schoolTestWorkbook?.id,
    ].join('_')
    this.schoolCode = data.schoolCode
    this.schoolName = data.schoolName
    this.revision = data.revision
    this.region = data.region
    this.schoolTestYear = data.schoolTestYear
    this.semester = data.semester
    this.schoolTestType = data.schoolTestType
    this.grade = data.grade
    this.schoolType = data.schoolType
    if (data.schoolTestWorkbook) {
      this.schoolTestWorkbook = new SchoolTestDomain.SchoolTestOrigin(data.schoolTestWorkbook)
    }
  }
}
